import VolumeTiersPanelDelegate from '../delegates/VolumeTiersPanel.delegate';
import TabController from './Tab.controller';
import Inherit from '../../../core/inherit';
import { DEFAULT_FEE_RULES } from '../configuration.constants';

/**
 * Sub-controller for the Volume Tiers panel inside the Fee Structure tab.
 *
 * Owns only the visual ordering state (`orderedIds`), which lives in the
 * host component's `state.tabs.volumeTiers` (via {@link TabController}).
 * The rule data itself lives in `state.tabs.feeStructure`; this controller
 * reads it through the parent's `state` getter when building view props
 * (during render, when state is committed) and receives explicit rule
 * arrays from the FeeStructure controller's add/remove methods so
 * `reconcileOrderedIds` can run in the same React batch.
 */
class VolumeTiersPanelController extends Inherit(TabController, VolumeTiersPanelDelegate) {
    constructor(parent) {
        super(parent.page || parent, 'volumeTiers');
        this.parent = parent;
    }

    /**
     * Default render slice the host seeds `state.tabs.volumeTiers` with.
     * Derived from the default rules so the order is valid before any FI
     * data has been applied.
     */
    getInitialState() {
        return { orderedIds: this.computeSortedIds(DEFAULT_FEE_RULES.volumeTierRules) };
    }

    /**
     * Called by the orchestrator on load with the volume-tier rules from the
     * already-computed fee structure slice. Returns the initial ordering
     * slice so the orchestrator can include it in the single load-time
     * `setState`.
     */
    applyFromFi(volumeTierRules = []) {
        return { orderedIds: this.computeSortedIds(volumeTierRules) };
    }

    // --- Reads from the parent (FeeStructure) slice --------------------
    // These are called during render when all state is committed, so
    // reading through the parent's `state` getter is correct.

    getRules() {
        return this.parent.state?.fee_rules?.volumeTierRules || [];
    }

    // --- Ordering / conflict logic -------------------------------------

    sortRulesByFromAmount(rules = []) {
        return [...rules].sort((a, b) => {
            const fa = Number(a.fromAmount);
            const fb = Number(b.fromAmount);
            const aValid = Number.isFinite(fa);
            const bValid = Number.isFinite(fb);
            if (!aValid && !bValid) return 0;
            if (!aValid) return 1;
            if (!bValid) return -1;
            return fa - fb;
        });
    }

    computeSortedIds(rules = []) {
        return this.sortRulesByFromAmount(rules).map((rule) => rule.id);
    }

    getRulesIdsKey(rules = []) {
        return rules.map((rule) => rule.id).join('|');
    }

    getOrderedRules() {
        const rules = this.getRules();
        const ruleMap = new Map(rules.map((rule) => [rule.id, rule]));
        const seen = new Set();
        const orderedRules = [];

        for (const id of this.state.orderedIds) {
            const rule = ruleMap.get(id);
            if (rule) {
                orderedRules.push(rule);
                seen.add(id);
            }
        }
        // Defensive: append any rules whose id isn't tracked yet (first
        // render before reconciliation, or freshly added rules).
        for (const rule of rules) {
            if (!seen.has(rule.id)) orderedRules.push(rule);
        }
        return orderedRules;
    }

    // Returns the set of rule ids that have an invalid range (`from > to`)
    // or that overlap with another rule. Adjacent tiers may share a single
    // boundary (e.g. 0-50 and 50-60).
    getConflictIds() {
        const rules = this.getRules();
        const ids = new Set();
        const parsed = rules.map((rule) => ({
            id: rule.id,
            from: Number(rule.fromAmount),
            to: Number(rule.toAmount)
        }));

        for (let i = 0; i < parsed.length; i += 1) {
            const a = parsed[i];
            const aValid = Number.isFinite(a.from) && Number.isFinite(a.to);
            if (aValid && a.from > a.to) ids.add(a.id);
            for (let j = i + 1; j < parsed.length; j += 1) {
                const b = parsed[j];
                if (!aValid || !Number.isFinite(b.from) || !Number.isFinite(b.to)) continue;
                if (a.from < b.to && b.from < a.to) {
                    ids.add(a.id);
                    ids.add(b.id);
                }
            }
        }
        return ids;
    }

    commitOrder() {
        this.setOrderedIds(this.computeSortedIds(this.getRules()));
    }

    /**
     * Called by FeeStructureController after add/remove with the
     * post-mutation rules array passed as an explicit argument. This avoids
     * reading in-flight state: the caller already has the new array as a
     * local variable before calling `appendFeeRule`/`removeFeeRule`.
     *
     * @param {Array} currentRules - the rules array after the mutation
     */
    reconcileOrderedIds(currentRules = []) {
        const currentKey = this.getRulesIdsKey(currentRules);
        const trackedKey = this.state.orderedIds.join('|');
        if (currentKey === trackedKey) return;
        this.setOrderedIds(this.computeSortedIds(currentRules));
    }

    // --- Chart bundle (read-only view of the rules) --------------------

    /**
     * Pre-cooked bundle for `FeeByVolumeChart` (consumed by the side
     * `AdvanceSummaryCard`). The chart stays purely presentational: this
     * method validates ranges, sorts ascending by `from`, and pre-computes
     * each segment's `widthRatio` (0..1) so the view just renders.
     */
    getChart() {
        const segments = this.getRules()
            .map((rule) => {
                const from = Number(rule.fromAmount);
                const to = Number(rule.toAmount);
                if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return null;
                // Inclusive width so `[0..50]` and `[51..100]` render side
                // by side at proportional sizes (51 wide and 50 wide).
                const width = Math.max(1, to - from + 1);
                return { id: rule.id, fee: rule.fee, from, to, width };
            })
            .filter(Boolean)
            .sort((a, b) => a.from - b.from);

        const totalWidth = segments.reduce((sum, s) => sum + s.width, 0) || 1;
        const segmentsWithRatio = segments.map(({ width, ...seg }) => ({
            ...seg,
            widthRatio: width / totalWidth
        }));
        const maxAmount = segments.length ? segments[segments.length - 1].to : 0;

        return { segments: segmentsWithRatio, maxAmount };
    }

    // --- View props -----------------------------------------------------

    getViewProps() {
        const conflictIds = this.getConflictIds();
        const orderedRules = this.getOrderedRules();
        const errorMessage =
            conflictIds.size > 0
                ? 'Volume tier ranges cannot overlap. Adjacent tiers may share a boundary (e.g. 0-50 and 50-60).'
                : undefined;

        return {
            orderedRules,
            conflictIds,
            errorMessage,
            onAdd: this.parent.addVolumeTierRule.bind(this.parent),
            onRemove: this.parent.removeVolumeTierRule.bind(this.parent),
            onUpdate: this.parent.updateVolumeTierRule.bind(this.parent),
            onCommitOrder: this.commitOrder.bind(this)
        };
    }
}

export default VolumeTiersPanelController;
